import React, { useState, useEffect, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Container } from 'react-bootstrap';
import { fetchMyTodos, fetchMyTodoCount, updateMyTodoStatus, setFilterValues, deleteMyTodo } from './store/my-todo.action';
import MyTodoList from './MyTodoList';
import MyTodoHeader from './Components/MyTodoHeader';
import { RootState, MY_TODO_FILTER_STORAGE_KEY } from './store/my-todo.types';
import type { MyTodoFilterState, MyTodoApplyFilterPayload } from './Components/MyTodoFilter';
import '../Todo.css';
import './MyTodo.css';

const EMPTY_FILTER_STATE: MyTodoFilterState = {
  activeStatus: [],
  activeBrand: [],
  activeProductType: [],
  activeCat1: [],
  activeCat2: [],
  activeCat3: [],
  activeCat4: [],
  activeSupplier: [],
  activeSeason: [],
  activeMonth: [],
  activeYear: [],
  activeCreatedBy: [],
  activeLicensor: [],
  activeLicense: [],
  activeLikeness: [],
  activeSubBrand: [],
  activeColorway: [],
  activeColorGroup: [],
  productStatus: [],
  dynamicTypes: [],
  textCustomAttribute: [],
};

function readPersistedFilter(): { filterState: MyTodoFilterState; activeTab: 'my-todo' | 'all-todo' } | null {
  try {
    const raw = localStorage.getItem(MY_TODO_FILTER_STORAGE_KEY);
    if (!raw) return null;
    return JSON.parse(raw) as { filterState: MyTodoFilterState; activeTab: 'my-todo' | 'all-todo' };
  } catch {
    return null;
  }
}

function persistFilter(filterState: MyTodoFilterState, activeTab: 'my-todo' | 'all-todo'): void {
  try {
    localStorage.setItem(MY_TODO_FILTER_STORAGE_KEY, JSON.stringify({ filterState, activeTab }));
  } catch (error) {
    console.error('Error persisting filter:', error);
  }
}


const MyTodo: React.FC = () => {
  const dispatch = useDispatch();

  const {
    MyTodoDetails: { items, totalCount, pendingCount, allTodoPendingCount, filterValues, loading },
    UserDetails: { userInfo, isSupplierUserLoggedIn },
  } = useSelector((state: RootState) => state);

  const [activeTab, setActiveTab] = useState<'my-todo' | 'all-todo'>(() => readPersistedFilter()?.activeTab ?? 'my-todo');
  const [searchText, setSearchText] = useState('');
  const [searchLoaded, setSearchLoaded] = useState(false);
  const [filterState, setFilterState] = useState<MyTodoFilterState>(() => readPersistedFilter()?.filterState ?? EMPTY_FILTER_STATE);

  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      if (searchLoaded) {
        dispatch(setFilterValues({ searchText, pageIndex: 1 }));
        setSearchLoaded(false);
      }
    }, 1000);
    return () => clearTimeout(delayDebounceFn);
  }, [searchText, searchLoaded, dispatch]);

  const fetchTodos = useCallback(async () => {
    const params = { ...filterValues, view: activeTab };
    if (activeTab === 'my-todo' && userInfo?.userId) {
      params.assigneeId = userInfo.userId;
    }
    if (!filterValues.statuses?.length) {
      delete params.statuses;
    }

    await dispatch(fetchMyTodos(params));
  }, [dispatch, filterValues, activeTab, userInfo?.userId]);

  useEffect(() => {
    fetchTodos();
  }, [fetchTodos]);

  useEffect(() => {
    const role = userInfo?.role;
    const cannotUseAllTodo = role === 'User' || role === 'SupplierUser' || Boolean(isSupplierUserLoggedIn);
    if (cannotUseAllTodo && activeTab === 'all-todo') {
      setActiveTab('my-todo');
      dispatch(
        setFilterValues({
          view: 'my-todo',
          pageIndex: 1,
          searchText: '',
        }),
      );
      setSearchText('');
    }
  }, [userInfo?.role, isSupplierUserLoggedIn, activeTab, dispatch]);

  const handleTabChange = (tab: 'my-todo' | 'all-todo') => {
    if (tab === activeTab) return;
    setActiveTab(tab);
    persistFilter(filterState, tab);
    dispatch(
      setFilterValues({
        view: tab,
        pageIndex: 1,
        searchText: '',
      }),
    );
    setSearchText('');
  };

  const handleSearchChange = (value: string) => {
    setSearchText(value);
    setSearchLoaded(true);
  };

  const handleApplyFilter = useCallback(
    (filterObj: MyTodoApplyFilterPayload) => {
      const newFilterState: MyTodoFilterState = {
        activeStatus: filterObj.status ?? [],
        activeBrand: filterObj.brandId ?? [],
        activeProductType: filterObj.productType ?? [],
        activeCat1: filterObj.catOneId ?? [],
        activeCat2: filterObj.catTwoId ?? [],
        activeCat3: filterObj.catThreeId ?? [],
        activeCat4: filterObj.catFourId ?? [],
        activeSupplier: filterObj.supplierId ?? [],
        activeSeason: filterObj.seasonId ?? [],
        activeMonth: filterObj.month ?? [],
        activeYear: filterObj.year ?? [],
        activeCreatedBy: filterObj.createdBy ?? [],
        activeLicensor: filterObj.licensorId ?? [],
        activeLicense: filterObj.licenseId ?? [],
        activeLikeness: filterObj.likenessId ?? [],
        activeSubBrand: filterObj.subBrandId ?? [],
        activeColorway: filterObj.colorwayId ?? [],
        activeColorGroup: filterObj.colorGroupId ?? [],
        productStatus: filterObj.productStatus ?? [],
        dynamicTypes: filterObj.dynamicTypes ?? [],
        textCustomAttribute: filterObj.textCustomAttribute ?? [],
      };
      setFilterState(newFilterState);
      persistFilter(newFilterState, activeTab);
      dispatch(
        setFilterValues({
          statuses: filterObj.status ?? [],
          brandId: filterObj.brandId ?? [],
          subBrandId: filterObj.subBrandId ?? [],
          productType: filterObj.productType ?? [],
          catOneId: filterObj.catOneId ?? [],
          catTwoId: filterObj.catTwoId ?? [],
          catThreeId: filterObj.catThreeId ?? [],
          catFourId: filterObj.catFourId ?? [],
          supplierId: filterObj.supplierId ?? [],
          seasonId: filterObj.seasonId ?? [],
          month: filterObj.month ?? [],
          year: filterObj.year ?? [],
          createdBy: filterObj.createdBy ?? [],
          productStatus: filterObj.productStatus ?? [],
          licensorId: filterObj.licensorId ?? [],
          licenseId: filterObj.licenseId ?? [],
          likenessId: filterObj.likenessId ?? [],
          colorwayId: filterObj.colorwayId ?? [],
          colorGroupId: filterObj.colorGroupId ?? [],
          dynamicTypes: filterObj.dynamicTypes ?? [],
          textCustomAttribute: filterObj.textCustomAttribute ?? [],
          pageIndex: 1,
        }),
      );
    },
    [dispatch, activeTab],
  );

  const handleStatusUpdate = async (id: string, status: 'Pending' | 'Done') => {
    try {
      await dispatch(updateMyTodoStatus({ id, status }));
      await fetchTodos();
      await dispatch(fetchMyTodoCount());
    } catch (error) {
      console.error('Error updating todo status:', error);
    }
  };

  const handleBulkStatusUpdate = async (ids: string[], status: 'Pending' | 'Done') => {
    try {
      await dispatch(updateMyTodoStatus({ ids, status }));
      await fetchTodos();
      await dispatch(fetchMyTodoCount());
    } catch (error) {
      console.error('Error updating bulk todo status:', error);
    }
  };

  const handleDelete = async (params: { id?: string; ids?: string[] }) => {
    try {
      await dispatch(deleteMyTodo(params));
      await fetchTodos();
    } catch (error) {
      console.error('Error deleting todo:', error);
    }
  };

  const handleLoadMore = () => {
    const nextPage = filterValues.pageIndex + 1;
    dispatch(
      setFilterValues({
        pageIndex: nextPage,
        isScrolled: true,
      }),
    );
  };

  return (
    <>
      <MyTodoHeader
        activeTab={activeTab}
        onTabChange={handleTabChange}
        searchText={searchText}
        onSearchChange={handleSearchChange}
        pendingCount={pendingCount}
        allTodoPendingCount={allTodoPendingCount}
        loading={loading}
        filterState={filterState}
        setFilterState={setFilterState}
        onApplyFilter={handleApplyFilter}
      />

      <Container fluid>
        <MyTodoList
          groups={items}
          loading={loading}
          totalCount={totalCount}
          currentPage={filterValues.pageIndex}
          pageSize={filterValues.pageSize}
          onStatusUpdate={handleStatusUpdate}
          onBulkStatusUpdate={handleBulkStatusUpdate}
          onDelete={handleDelete}
          onLoadMore={handleLoadMore}
          showAssigneeColumn={activeTab === 'all-todo'}
          viewType={activeTab}
        />
      </Container>
    </>
  );
};

export default MyTodo;
